'use client';
// ────────────────────────────────────────────────────────────
// useMessageActions — shared AI-message action state + handlers.
//
// Owns the per-customer reaction map, the overflow-menu open/close
// index, the document-level outside-click / Escape dismiss, and the
// /api/mobile/chat/feedback hydration + POST. Consumed by both the
// desktop ChatDrawer and the mobile /m/q page so the two surfaces
// share one source of truth for like/dislike/copy/menu behavior.
//
// Extracted from ChatDrawer.tsx without behavioral change:
//   - optimistic-then-confirm reaction toggle (rolls back on non-2xx)
//   - anon_* customers skip the POST but still flip local state
//   - GET hydration runs once when isReady flips true and customerId
//     is a real (non-anon_) id, silently ignoring network errors
//   - memory menu items remain console.log stubs (unchanged from
//     ChatDrawer.tsx:913-919 — backend integration deferred)
// ────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { isMobileOtpVerified, clearMobileOtpVerified } from './onboarding';

export type MessageReaction = 'like' | 'dislike' | null;

export interface UseMessageActionsParams {
    customerId: string | null;
    // Session key sent with the feedback POST. Desktop uses the
    // localStorage-minted `kiosk-*` id; mobile uses `mobile-${customerId}`
    // so the row matches the backend session convention. When null,
    // the hook skips POST (hydration still runs if customerId is real).
    sessionId: string | null;
    // Gate that lets each surface control when hydration is safe.
    // Desktop flips this true after transcript restore settles so the
    // GET never races a still-empty bubble list; mobile can flip it
    // immediately on mount.
    isReady: boolean;
}

export interface MessageActionsHandle {
    messageReactions: Record<string, MessageReaction>;
    openMenuIdx: number | null;
    setOpenMenuIdx: Dispatch<SetStateAction<number | null>>;
    handleCopyMessage: (text: string) => Promise<void>;
    handleToggleReaction: (
        kind: 'like' | 'dislike',
        messageId: string | undefined,
        messageText: string,
    ) => Promise<void>;
    handleSaveMemory: (text: string) => void;
    handleRemoveMemory: (text: string) => void;
}

export function useMessageActions({
    customerId,
    sessionId,
    isReady,
}: UseMessageActionsParams): MessageActionsHandle {
    // Keyed by the AI bubble's stable messageId (same id that rides with
    // the POST /mobile/chat/feedback call and with the persisted
    // transcript). String keys (not array indices) so: (a) a refresh +
    // transcript restore re-applies reactions to the same bubbles,
    // (b) hydration from GET /mobile/chat/feedback can set the map
    // directly, and (c) future reorders of messages never mis-highlight
    // an unrelated bubble.
    const [messageReactions, setMessageReactions] = useState<Record<string, MessageReaction>>({});

    // Index of the AI message whose 3-dot menu is open, or null.
    // Only one menu open at a time; the listener below closes it on
    // outside click or Escape.
    const [openMenuIdx, setOpenMenuIdx] = useState<number | null>(null);

    useEffect(() => {
        if (openMenuIdx === null) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest('.qio-action-menu')) return;
            setOpenMenuIdx(null);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenuIdx(null); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [openMenuIdx]);

    // Hydrate prior reactions for this customer. Silent-fail: any
    // network / auth error just leaves the local map empty — the user
    // can still click fresh reactions during the session and the POST
    // path will persist them.
    //
    // Skipping when the tab hasn't completed mobile OTP avoids a
    // console-visible 401 on every mount: `qcare_customer_id` can be
    // restored from localStorage but the matching `qio_session` cookie
    // is httpOnly and may already be expired. Without the gate, the GET
    // fires once per drawer mount, the proxy returns 401, and DevTools
    // logs the network error even though the app handles it silently.
    useEffect(() => {
        if (!isReady) return;
        if (!customerId || customerId.startsWith('anon_')) return;
        if (!isMobileOtpVerified()) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('/api/mobile/chat/feedback', {
                    method: 'GET',
                    credentials: 'include',
                });
                if (res.status === 401) {
                    // Server says the cookie is no longer valid (expired,
                    // signed with an old secret, etc.). Drop the flag so
                    // re-mounts in this tab don't keep firing the same
                    // losing GET — the user re-OTPs to restore hydration.
                    clearMobileOtpVerified();
                    return;
                }
                if (!res.ok) return;
                const data = await res.json().catch(() => null) as
                    | { items?: Array<{ messageId: string; feedbackType: 'like' | 'dislike' }> }
                    | null;
                if (cancelled || !data?.items || !Array.isArray(data.items)) return;
                const next: Record<string, MessageReaction> = {};
                for (const item of data.items) {
                    if (item && typeof item.messageId === 'string'
                        && (item.feedbackType === 'like' || item.feedbackType === 'dislike')) {
                        next[item.messageId] = item.feedbackType;
                    }
                }
                // Merge server-side reactions under any unsaved local clicks
                // the user may have made between mount and GET resolution.
                setMessageReactions(prev => ({ ...next, ...prev }));
            } catch {
                // network / parser error — leave state untouched
            }
        })();
        return () => { cancelled = true; };
    }, [isReady, customerId]);

    const handleCopyMessage = useCallback(async (text: string) => {
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            }
        } catch {
            // clipboard disabled — fall through silently
        }
    }, []);

    const handleToggleReaction = useCallback(async (
        kind: 'like' | 'dislike',
        messageId: string | undefined,
        messageText: string,
    ) => {
        // No stable id → nothing to persist and nothing to echo across
        // refresh, so drop the click entirely rather than flipping an
        // untracked highlight the user could never re-sync.
        if (!messageId) return;
        // Optimistic UI — same-kind click toggles off, opposite-kind switches.
        const prevState = messageReactions[messageId] ?? null;
        const nextState: MessageReaction = prevState === kind ? null : kind;
        setMessageReactions(prev => ({ ...prev, [messageId]: nextState }));

        // Only persist for authenticated customers. Anonymous sessions
        // cannot satisfy the backend's qio_session gate, so we skip the
        // POST — but keep the optimistic highlight so the UI still feels
        // responsive in that path.
        if (!customerId || customerId.startsWith('anon_')) return;
        if (!sessionId) return;
        try {
            const res = await fetch('/api/mobile/chat/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    messageId,
                    sessionId,
                    feedbackType: kind,
                    messagePreview: messageText.slice(0, 500),
                }),
            });
            if (!res.ok) {
                setMessageReactions(prev => ({ ...prev, [messageId]: prevState }));
                return;
            }
            const data = await res.json().catch(() => null) as { state?: 'liked' | 'disliked' | 'none' } | null;
            if (data?.state === 'liked' || data?.state === 'disliked' || data?.state === 'none') {
                const final: MessageReaction =
                    data.state === 'liked' ? 'like'
                        : data.state === 'disliked' ? 'dislike'
                            : null;
                setMessageReactions(prev => ({ ...prev, [messageId]: final }));
            }
        } catch {
            setMessageReactions(prev => ({ ...prev, [messageId]: prevState }));
        }
    }, [messageReactions, customerId, sessionId]);

    // Memory menu actions — currently stubs on desktop (see
    // ChatDrawer.tsx:913-919 prior to extraction). Preserved as-is so
    // mobile parity mirrors current desktop truth, not a fantasy future.
    const handleSaveMemory = useCallback((text: string) => {
        console.log('SAVE_MEMORY', text);
    }, []);

    const handleRemoveMemory = useCallback((text: string) => {
        console.log('REMOVE_MEMORY', text);
    }, []);

    return {
        messageReactions,
        openMenuIdx,
        setOpenMenuIdx,
        handleCopyMessage,
        handleToggleReaction,
        handleSaveMemory,
        handleRemoveMemory,
    };
}
